<template>
    <div class="searchable-select">
        <div class="select-header" @click="toggleDropdown">
            <span v-if="selectedOptionLabel">{{ selectedOptionLabel }}</span>
            <span v-else class="placeholder">{{ placeholder }}</span>
            <span class="arrow">▼</span>
        </div>

        <div v-show="isOpen" class="dropdown">
            <div class="search-container">
                <input
                    ref="searchInput"
                    v-model="searchTerm"
                    type="text"
                    placeholder="Поиск..."
                    class="search-input"
                    @keydown.esc="isOpen = false"
                />
            </div>

            <ul v-if="filteredOptions.length" class="options-list">
                <li
                    v-for="option in filteredOptions"
                    :key="option.value"
                    class="option-item"
                    @click="selectOption(option)"
                >
                    {{ option.label }}
                </li>
            </ul>

            <div v-else class="no-results">Ничего не найдено</div>
        </div>
    </div>
</template>

<script>
    import { ref, computed, watch, nextTick } from 'vue'

    export default {
        props: {
            options: {
                type: Array,
                required: true,
                default: () => []
            },
            placeholder: {
                type: String,
                default: 'Выберите значение'
            },
            modelValue: {
                type: [String, Number],
                default: ''
            }
        },
        emits: ['update:modelValue'],
        setup(props, { emit }) {
            const isOpen = ref(false)
            const searchTerm = ref('')
            const searchInput = ref(null)

            const normalizedOptions = computed(() => {
                return props.options.map(option => {
                    if (typeof option === 'string' || typeof option === 'number') {
                        return { value: option, label: String(option) }
                    }
                    return option
                })
            })

            const selectedOptionLabel = computed(() => {
                if (!props.modelValue) return ''
                const option = normalizedOptions.value.find(opt => opt.value === props.modelValue)
                return option ? option.label : ''
            })

            const filteredOptions = computed(() => {
                if (!searchTerm.value) return normalizedOptions.value

                const term = searchTerm.value.toLowerCase()
                return normalizedOptions.value.filter(option =>
                    option.label.toLowerCase().includes(term)
                )
            })

            function toggleDropdown() {
                isOpen.value = !isOpen.value
                if (isOpen.value) {
                    nextTick(() => {
                        searchInput.value?.focus()
                    })
                } else {
                    searchTerm.value = ''
                }
            }

            function selectOption(option) {
                emit('update:modelValue', option.value)
                isOpen.value = false
                searchTerm.value = ''
            }

            function handleClickOutside(event) {
                if (!event.target.closest('.searchable-select')) {
                    isOpen.value = false
                    searchTerm.value = ''
                }
            }

            watch(isOpen, newValue => {
                if (newValue) {
                    document.addEventListener('click', handleClickOutside)
                } else {
                    document.removeEventListener('click', handleClickOutside)
                }
            })

            return {
                isOpen,
                searchTerm,
                searchInput,
                selectedOptionLabel,
                filteredOptions,
                toggleDropdown,
                selectOption
            }
        }
    }
</script>

<style scoped>
    .searchable-select {
        position: relative;
        width: 100%;
        height: 42px;
    }

    .select-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        background: var(--card-bg);
        cursor: pointer;
        height: 100%;
    }

    .placeholder {
        color: #888;
    }

    .arrow {
        font-size: 12px;
    }

    .dropdown {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        background: white;
        border: 1px solid #d0d9e7;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        z-index: 100;
        margin-top: 5px;
        max-height: 300px;
        overflow: hidden;
    }

    .search-container {
        padding: 10px;
        border-bottom: 1px solid #eee;
    }

    .search-input {
        width: 100%;
        padding: 8px 12px;
        border: 1px solid #ddd;
        border-radius: 4px;
    }

    .options-list {
        max-height: 250px;
        overflow-y: auto;
        list-style: none;
        padding: 0;
        margin: 0;
    }

    .option-item {
        padding: 10px 15px;
        cursor: pointer;
    }

    .option-item:hover {
        background-color: #f0f4ff;
    }

    .no-results {
        padding: 15px;
        text-align: center;
        color: #888;
    }
</style>
